// ── Native system-bar tinting (Android) ─────────────────────────────────────
// Wraps the custom ClarityBarsPlugin (android/app/.../ClarityBarsPlugin.java).
//
// On the web, <meta name="theme-color"> is enough to make the browser chrome
// blend into the page. Inside the Capacitor WebView it does nothing: we target
// SDK 35+, so Android forces edge-to-edge, and Capacitor 8 handles that by
// padding the decor view down by the system-bar insets. The strips that leaves
// above and below the WebView are painted by the decor view's background,
// which Capacitor sets once from the theme's windowBackground (AppCompat's
// off-white) — a pale band across the top of every screen and another behind
// the gesture bar. This hands the active theme's gradient endpoints to the
// native side so those strips take on the app's own colours instead.
//
// The plugin name matters: Capacitor 8 has a BUILT-IN plugin called
// "SystemBars", and registerPlugin('SystemBars') would quietly hand back
// core's proxy instead of ours, so every call would throw "not implemented".
// Hence "ClarityBars". Don't rename it back.
import { registerPlugin, Capacitor } from '@capacitor/core';
import { barColorsFor } from '../theme/colorEngine';

const ClarityBars = registerPlugin('ClarityBars');
const isNative = Capacitor.isNativePlatform();

// Perceived lightness, used to decide whether the clock/battery icons drawn on
// top of a strip should be dark or light. Plain sRGB luma is enough here — the
// call is only ever "is this strip light or dark", never a contrast ratio.
function isLight(hex) {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length === 8) h = h.slice(0, 6);       // drop any alpha
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return true;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
}

// Skip redundant native round-trips: this is called from a theme effect that
// can re-run on unrelated re-renders, and repainting the window background is
// not free.
let lastSent = '';

// `bgTheme` is the active theme's key (e.g. "peach", "mist") and `rootBg` its
// root background. barColorsFor() resolves those to the colours the page really
// shows at its top and bottom edge — reading a gradient's end stops directly,
// or looking up a measured pair for the themes whose look comes from a blurred
// radial mesh rather than the gradient string. Safe to call on the web — it
// just no-ops.
export function syncClarityBars(bgTheme, rootBg) {
  if (!isNative) return;
  const ends = barColorsFor(bgTheme, rootBg);
  if (!ends) return;
  const payload = {
    top: ends.top,
    bottom: ends.bottom,
    darkStatusIcons: isLight(ends.top),
    darkNavIcons: isLight(ends.bottom),
  };
  const key = JSON.stringify(payload);
  if (key === lastSent) return;
  lastSent = key;
  // Never throw out of a render effect over a status-bar tint — but don't
  // swallow the reason either. A silently-eaten rejection here is exactly what
  // hid the "SystemBars" name collision above through three rounds of testing:
  // the native side was never running and nothing said so.
  ClarityBars.setBarColors(payload).catch((err) => {
    lastSent = '';
    console.warn('[clarityBars] native tint failed:', err);
  });
}

// Turns Android autofill (Gboard's password / card / address strip above the
// keyboard) off while the Planner quick-add bar is open — see
// ClarityBarsPlugin.setAutofill. No-op on the web and on builds without it.
export function setNativeAutofill(enabled) {
  if (!isNative) return;
  ClarityBars.setAutofill({ enabled }).catch(() => {});
}
